import { useEffect, useState } from "react";
import { Heart, Instagram, MessageCircle, Play } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const INSTAGRAM_URL = "https://www.instagram.com/gamelensphotos/";
// Drop a provider-generated embed URL (Elfsight / SociableKIT) into
// frontend/.env as REACT_APP_INSTAGRAM_WIDGET_URL and this section
// upgrades to a full widget frame. Otherwise it renders the live feed
// from the backend's SocialKit proxy, falling back to the curated grid.
const WIDGET_URL = process.env.REACT_APP_INSTAGRAM_WIDGET_URL;
const LIVE_FEED_URL = `${process.env.REACT_APP_BACKEND_URL}/api/instagram/feed`;

const TILES = [
  { src: "/images/feed-1.jpg", alt: "Batter swinging at a pitch" },
  { src: "/images/feed-2.jpg", alt: "Teammates celebrating a big play" },
  { src: "/images/feed-3.jpg", alt: "Pitcher winding up under the palms" },
  { src: "/images/feed-4.jpg", alt: "Runner diving toward home plate" },
  { src: "/images/feed-5.jpg", alt: "Pitcher delivering from the mound" },
];

function LiveTile({ post, index }) {
  return (
    <a
      href={post.url || INSTAGRAM_URL}
      target="_blank"
      rel="noreferrer"
      data-testid={`instagram-live-tile-${index + 1}`}
      className="group relative block aspect-square overflow-hidden rounded-xl border border-white/5"
    >
      <img
        src={post.thumbnail_url}
        alt={post.caption ? post.caption.slice(0, 100) : "Latest from Instagram"}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      {post.video_url && (
        <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-ink/70 text-white">
          <Play className="h-3.5 w-3.5" />
        </span>
      )}
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <Instagram className="h-7 w-7 text-accentlight" />
        <span className="flex items-center gap-3 text-xs text-white/80">
          <span className="flex items-center gap-1">
            <Heart className="h-3.5 w-3.5" /> {post.likes}
          </span>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-3.5 w-3.5" /> {post.comments}
          </span>
        </span>
      </span>
    </a>
  );
}

function FollowTile() {
  return (
    <a
      href={INSTAGRAM_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="instagram-follow-tile"
      className="group flex aspect-square flex-col items-center justify-center gap-3 rounded-xl border border-accentlight/30 bg-surface/60 p-4 text-center transition-colors duration-300 hover:bg-surface"
    >
      <Instagram className="h-8 w-8 text-accentlight transition-transform duration-300 group-hover:scale-110" />
      <span className="font-display text-lg leading-tight tracking-wider">
        @GAMELENS
        <br />
        PHOTOS
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/40">
        Follow the feed
      </span>
    </a>
  );
}

export default function InstagramFeed() {
  const [livePosts, setLivePosts] = useState(null);

  useEffect(() => {
    if (WIDGET_URL) return;
    let active = true;
    fetch(LIVE_FEED_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("feed unavailable"))))
      .then((data) => {
        if (active) setLivePosts((data.items || []).slice(0, 5));
      })
      .catch(() => {
        if (active) setLivePosts([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const showLive = Array.isArray(livePosts) && livePosts.length > 0;

  return (
    <section id="feed" className="relative py-24 md:py-36" data-testid="instagram-section">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <SectionHeading
          eyebrow="From the feed"
          title={
            <>
              FRESH FROM
              <br />
              <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                THE FEED
              </span>
            </>
          }
        />

        <Reveal delay={0.15}>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/60 md:text-base">
            Game-day highlights the moment they happen. Follow along — first look at every gallery
            drops on Instagram.
          </p>
        </Reveal>

        {WIDGET_URL ? (
          <Reveal delay={0.2}>
            <iframe
              title="Latest photos from @gamelensphotos"
              src={WIDGET_URL}
              loading="lazy"
              sandbox="allow-scripts allow-popups allow-same-origin"
              referrerPolicy="strict-origin-when-cross-origin"
              className="mt-10 min-h-[620px] w-full rounded-2xl border-0"
              data-testid="instagram-widget-frame"
            />
          </Reveal>
        ) : (
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {showLive
              ? livePosts.map((p, i) => (
                  <Reveal key={p.id || p.url} delay={0.1 + i * 0.07}>
                    <LiveTile post={p} index={i} />
                  </Reveal>
                ))
              : TILES.map((t, i) => (
                  <Reveal key={t.src} delay={0.1 + i * 0.07}>
                    <a
                      href={INSTAGRAM_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`instagram-tile-${i + 1}`}
                      className="group relative block aspect-square overflow-hidden rounded-xl border border-white/5"
                    >
                      <img
                        src={t.src}
                        alt={t.alt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-ink/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <Instagram className="h-7 w-7 text-accentlight" />
                      </span>
                    </a>
                  </Reveal>
                ))}
            <Reveal delay={0.47}>
              <FollowTile />
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
